import { CalendarRegular, ArrowRightRegular } from "@fluentui/react-icons";
import React, { useState, useEffect } from "react";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { setMinutes, addMinutes, setHours, endOfDay } from "date-fns";
import { getDifferenceInMinutes, getMeetingMinTime, isSameDay } from "../../shared/utils/dateUtils";
import { constants } from "../../shared/constants";
import styles from "./dateField.module.scss";
import { useTranslation } from "../../shared/translation/Translation";

interface DateFieldProps {
    startingDate: Date;
    endingDate: Date;
    minDate: Date;
    maxDate: Date;
    isInvalid?: boolean;
    errorMessage?: string;

    setStartingDate: (value: Date) => void;
    setEndingDate: (value: Date) => void;
}

const DateField: React.FC<DateFieldProps> = (props: DateFieldProps) => {
    const messages = useTranslation();

    const [meetingDuration, setMeetingDuration] = useState<number>(constants.defaultMeetingDurationInMinutes);

    const handleCalendarDayChange = (date: any) => {
        const startingDate = props.startingDate.getHours();
        const startingMinutes = props.startingDate.getMinutes();

        const dateWithHours = setHours(date, startingDate);
        const dateWithMinutes = setMinutes(dateWithHours, startingMinutes);

        handleStartingDateChange(dateWithMinutes);
    };

    const handleStartingDateChange = (date: any) => {
        if (date < new Date()) {
            date = getMeetingMinTime(date);
        }

        props.setStartingDate(date as Date);

        const minutes = getDifferenceInMinutes(endOfDay(date), date);
        if (meetingDuration > minutes) {
            props.setEndingDate(addMinutes(date, minutes));
        } else {
            props.setEndingDate(addMinutes(date, meetingDuration));
        }
    };

    const handleStartingHourChange = (date: any) => {
        if (!date) {
            return;
        }

        const startingDate = date.getHours();
        const startingMinutes = date.getMinutes();

        const dateWithHours = setHours(props.startingDate, startingDate);
        const dateWithMinutes = setMinutes(dateWithHours, startingMinutes);

        handleStartingDateChange(dateWithMinutes);
    };

    const handleEndingHourChange = (date: any) => {
        if (!date) {
            return;
        }

        const endingDate = date.getHours();
        const endingMinutes = date.getMinutes();

        const dateWithHours = setHours(props.endingDate, endingDate);
        const newDate = setMinutes(dateWithHours, endingMinutes);

        if (newDate < props.startingDate) {
            setMeetingDuration(0);
            props.setEndingDate(props.startingDate);
            return;
        }

        let difference = getDifferenceInMinutes(newDate, props.startingDate);
        setMeetingDuration(difference);
        props.setEndingDate(newDate as Date);
    };

    const getMinTime = () => {
        if (props.startingDate > props.minDate && props.startingDate.getDate() !== props.minDate.getDate()) {
            const minTime = new Date(props.startingDate);
            minTime.setHours(0, 0, 0, 0);
            return minTime;
        }
        return props.minDate;
    };

    const getMaxTime = () => {
        if (isSameDay(props.startingDate, props.maxDate)) {
            return props.maxDate;
        }
        return endOfDay(props.startingDate);
    };

    return (
        <div className={styles.dateFieldWrapper}>
            <CalendarRegular style={{ fontSize: "24px" }} />
            <div>
                <div className={styles.dateFieldControl}>
                    <DatePicker
                        wrapperClassName={styles.datePickerWrapper}
                        selected={props.startingDate}
                        onChange={(date) => handleCalendarDayChange(date)}
                        minDate={props.minDate}
                        maxDate={props.maxDate}
                        dateFormat={constants.dateFormat}
                        className={styles.datePicker}
                    />
                    <DatePicker
                        wrapperClassName={styles.datePickerWrapper}
                        selected={props.startingDate}
                        onChange={(date) => handleStartingHourChange(date)}
                        showTimeSelect
                        showTimeSelectOnly
                        timeIntervals={constants.defaultMeetingIntervalInMinutes}
                        dateFormat={constants.timeFormat}
                        timeFormat={constants.timeFormat}
                        timeCaption={messages.meetingModals.startingHourLabel}
                        minTime={getMinTime()}
                        maxTime={getMaxTime()}
                    />
                    <ArrowRightRegular className={styles.arrowIcon} />
                    <DatePicker
                        wrapperClassName={styles.datePickerWrapper}
                        selected={props.endingDate}
                        onChange={(date) => handleEndingHourChange(date)}
                        showTimeSelect
                        showTimeSelectOnly
                        timeIntervals={constants.defaultMeetingIntervalInMinutes}
                        timeCaption={messages.meetingModals.endingHourLabel}
                        dateFormat={constants.timeFormat}
                        timeFormat={constants.timeFormat}
                        minTime={props.startingDate}
                        maxTime={getMaxTime()}
                    />
                </div>
                <p className={styles.errorMessage}>{props.isInvalid && props.errorMessage}</p>
            </div>
        </div>
    );
};

export default DateField;
